import {
  getUserSifarishDetails,
  acceptBusinessProfileRequest,
  rejectBusinessProfileRequest,
} from "_services";
import { getCategories } from "_services";
import { ButtonHeading } from "components";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Images } from "constants";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { utils } from "helper";
import useDocumentTitle from "hooks/useDocumentTitle";

const CONTENT_CATEGORY_ID = "6783b12f2767a48108c86780";

const BusinessProfileRequestDetail = () => {
  useDocumentTitle("Business Request | MySifarish");
  const { sifarishId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [sifarishDetails, setSifarishDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("userData");
    if (stored) setUserData(JSON.parse(stored));
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await getCategories();
        const list = res?.data?.categories || res?.categories || res?.data || [];
        const filtered = Array.isArray(list)
          ? list.filter((c) => c._id !== CONTENT_CATEGORY_ID)
          : [];
        setCategories(filtered);
      } catch (e) {
        console.warn("Categories load failed", e);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    if (!userData?._id || !sifarishId) return;
    fetchDetail();
  }, [userData?._id, sifarishId]);

  useEffect(() => {
    if (sifarishDetails) {
      const catId = sifarishDetails?.categoryId?._id || sifarishDetails?.categoryId;
      setSelectedCategoryId(catId || "");
      setSelectedSubCategoryId(sifarishDetails?.subCategoryId || "");
    }
  }, [sifarishDetails]);

  const fetchDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUserSifarishDetails({
        userId: userData._id,
        sifarishId,
      });
      if (res?.data?.sifarish) {
        const sif = res.data.sifarish;
        if (sif.isDeleted || !sif.isRegisteredByBusiness) {
          setError("This request is no longer available.");
          setSifarishDetails(null);
        } else {
          setSifarishDetails(sif);
        }
      } else {
        setError(res?.responseMessage || "Not found.");
      }
    } catch (err) {
      setError(err?.message || "Something went wrong");
      toast.error(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const subCategoryList = selectedCategoryId
    ? (categories.find((c) => c._id === selectedCategoryId)?.subCategories || [])
    : [];

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    draggable: true,
  };

  const allImages = () => {
    const list = [];
    if (sifarishDetails?.logoUrl) {
      list.push({ url: sifarishDetails.logoUrl, label: "Logo" });
    }
    (sifarishDetails?.mediaUrls || []).forEach((m) => list.push(m));
    if (sifarishDetails?.businessProofUrl) {
      list.push({ url: sifarishDetails.businessProofUrl, label: "Business proof" });
    }
    return list;
  };

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      const payload = {
        userId: userData._id,
        sifarishId,
      };
      if (selectedCategoryId && selectedCategoryId !== (sifarishDetails?.categoryId?._id || sifarishDetails?.categoryId)) {
        payload.categoryId = selectedCategoryId;
        payload.subCategoryId = subCategoryList.length > 0 ? selectedSubCategoryId || null : null;
      }
      const res = await acceptBusinessProfileRequest(payload);
      if (res?.status === 1) {
        toast.success(res?.message || "Accepted.");
        navigate("/business_profile_requests");
      } else {
        toast.error(res?.message || "Failed to accept.");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm("Are you sure you want to reject this business request? This will mark it as deleted.")) return;
    setActionLoading(true);
    try {
      const res = await rejectBusinessProfileRequest({
        userId: userData._id,
        sifarishId,
      });
      if (res?.status === 1) {
        toast.success(res?.message || "Rejected.");
        navigate("/business_profile_requests");
      } else {
        toast.error(res?.message || "Failed to reject.");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBack = () => navigate("/business_profile_requests");

  if (loading) {
    return (
      <>
        <ButtonHeading title="Business Request" isShow={{ isBackButton: true }} handleBackButton={handleBack} />
        <p className="p-4">Loading...</p>
      </>
    );
  }

  if (error && !sifarishDetails) {
    return (
      <>
        <ButtonHeading title="Business Request" isShow={{ isBackButton: true }} handleBackButton={handleBack} />
        <p className="p-4 text-danger">{error}</p>
      </>
    );
  }

  const images = allImages();

  return (
    <>
      <ButtonHeading
        title="Business Request"
        isShow={{ isBackButton: true }}
        handleBackButton={handleBack}
      />

      <div className="px-3 py-4">
        <div className="__sifarisD_head mb-3">
          <div className="__sifDetail_bxs">
            <h4>Title: {sifarishDetails?.title || "—"}</h4>
            <p>Description: {sifarishDetails?.description || "—"}</p>
          </div>
        </div>

        {/* Category & Subcategory */}
        <div className="mb-4">
          <label className="form-label fw-bold">Category</label>
          <select
            className="form-select mb-2"
            value={selectedCategoryId}
            onChange={(e) => {
              setSelectedCategoryId(e.target.value);
              setSelectedSubCategoryId("");
            }}
          >
            <option value="">— Select category —</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>
                {c.title}
              </option>
            ))}
          </select>
          {subCategoryList.length > 0 && (
            <>
              <label className="form-label fw-bold">Subcategory</label>
              <select
                className="form-select"
                value={selectedSubCategoryId}
                onChange={(e) => setSelectedSubCategoryId(e.target.value)}
              >
                <option value="">— Select subcategory —</option>
                {subCategoryList.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.title}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>

        {/* Small images + lightbox */}
        {images.length > 0 && (
          <div className="mb-4">
            <h6 className="mb-2">Images</h6>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              {images.map((img, index) => (
                <div key={index} style={{ width: 80, height: 60 }}>
                  <img
                    src={img?.url || "https://via.placeholder.com/80x60"}
                    alt={img?.label || ""}
                    className="_slidImg"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      cursor: "pointer",
                      borderRadius: 4,
                    }}
                    onClick={() => {
                      setCurrentImageIndex(index);
                      setLightboxOpen(true);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full detail */}
        <div className="__userView mb-4">
          {sifarishDetails?.logoUrl && (
            <div className="mb-2">
              <strong>Logo:</strong>{" "}
              <a href={sifarishDetails.logoUrl} target="_blank" rel="noopener noreferrer">
                View
              </a>
            </div>
          )}
          {sifarishDetails?.offering?.length > 0 && (
            <div className="mb-2">
              <strong>Offering:</strong> {sifarishDetails.offering.join(", ")}
            </div>
          )}
          {sifarishDetails?.contactPersonRole && (
            <div className="mb-2">
              <strong>Contact person role:</strong> {sifarishDetails.contactPersonRole}
            </div>
          )}
          <div className="mb-2">
            <strong>Contact name:</strong> {sifarishDetails?.contactName || "—"}
          </div>
          <div className="mb-2">
            <strong>Phone:</strong> {sifarishDetails?.phoneNumber || "—"}
          </div>
          <div className="mb-2">
            <strong>Email:</strong> {sifarishDetails?.email || "—"}
          </div>
          {sifarishDetails?.address && (
            <div className="mb-2">
              <strong>Address:</strong>{" "}
              {[
                sifarishDetails.address.addressLine1,
                sifarishDetails.address.city,
                sifarishDetails.address.state,
                sifarishDetails.address.country,
                sifarishDetails.address.zipcode,
              ]
                .filter(Boolean)
                .join(", ") || "—"}
            </div>
          )}
          {sifarishDetails?.businessProofUrl && (
            <div className="mb-2">
              <strong>Business proof:</strong>{" "}
              <a href={sifarishDetails.businessProofUrl} target="_blank" rel="noopener noreferrer">
                View
              </a>
            </div>
          )}
          <div className="mb-2">
            <strong>Created:</strong>{" "}
            {sifarishDetails?.createdAt
              ? utils?.formatDateToDDMMYYYY(sifarishDetails.createdAt)
              : "—"}
          </div>
          {sifarishDetails?.createdByUserId && (
            <div className="mb-2">
              <strong>Created by:</strong>{" "}
              {sifarishDetails.createdByUserId.firstName} {sifarishDetails.createdByUserId.lastName}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="d-flex gap-2">
          <button
            className="__birdTag btn-success text-white"
            onClick={handleAccept}
            disabled={actionLoading}
          >
            {actionLoading ? "Please wait..." : "Accept"}
          </button>
          <button
            className="__birdTag btn-danger text-white"
            onClick={handleReject}
            disabled={actionLoading}
          >
            Reject
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setLightboxOpen(false);
            if (e.key === "ArrowLeft")
              setCurrentImageIndex((prev) => Math.max(prev - 1, 0));
            if (e.key === "ArrowRight")
              setCurrentImageIndex((prev) => Math.min(prev + 1, images.length - 1));
          }}
          tabIndex={0}
          role="button"
        >
          <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="lightbox-close"
              onClick={() => setLightboxOpen(false)}
            >
              ✕
            </button>
            <Slider {...sliderSettings} initialSlide={currentImageIndex}>
              {images.map((item, index) => (
                <div key={index}>
                  <img
                    src={item?.url || "https://via.placeholder.com/800x600"}
                    alt=""
                    className="lightbox-image"
                  />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      )}
    </>
  );
};

export default BusinessProfileRequestDetail;
